import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { Layout as AntLayout, Menu, Button, Typography, Dropdown, Drawer } from "antd";
import {
  BookOutlined,
  ContainerOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { useAgentProfile } from "../hooks/useAgentProfile";
import { useUser } from "../hooks/useUser";
import { AGENT_ROUTES } from "../config/agentPortal";
import { AGENT_MOBILE_BREAKPOINT_PX } from "../config/agentPortalLayout";

const { Sider, Content, Header } = AntLayout;
const { Text } = Typography;

const MOBILE_MQ = `@media (max-width: ${AGENT_MOBILE_BREAKPOINT_PX - 1}px)`;

const StyledLayout = styled(AntLayout)`
  min-height: 100vh;
  overflow-x: hidden;
`;

const DesktopSider = styled(Sider)`
  ${MOBILE_MQ} {
    display: none !important;
  }
`;

const InnerLayout = styled(AntLayout)`
  min-height: 100vh;
  flex: 1;
  min-width: 0;
`;

const LogoArea = styled.div<{ $compact?: boolean }>`
  padding: ${(props) => (props.$compact ? "12px 8px" : "16px 12px")};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;

  img {
    height: ${(props) => (props.$compact ? "28px" : "40px")};
    width: auto;
    max-width: 100%;
    display: block;
    margin: 0 auto 8px;
    object-fit: contain;
  }
`;

const PortalLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  display: block;
`;

const MainContent = styled(Content)`
  margin: 0;
  padding: 24px;
  background: #f0f2f5;
  flex: 1;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: 767px) {
    padding: 12px;
    padding-bottom: 24px;
  }
`;

const TopHeader = styled(Header)`
  background: #fff;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 100;
  line-height: 1.2;
  height: 56px;

  @media (max-width: 767px) {
    padding: 0 12px;
  }
`;

const HeaderStart = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const MobileMenuButton = styled(Button)`
  display: none;

  ${MOBILE_MQ} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
`;

const AgentName = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 575px) {
    font-size: 13px;
  }
`;

const UserButton = styled(Button)`
  max-width: 220px;

  span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 575px) {
    max-width: 40px;
    padding-inline: 8px;

    span:last-child {
      display: none;
    }
  }
`;

const MobileDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
    background: #001529;
  }
`;

const navItems = [
  {
    key: AGENT_ROUTES.bookings,
    icon: <BookOutlined />,
    label: "Bookings",
  },
  {
    key: AGENT_ROUTES.containers,
    icon: <ContainerOutlined />,
    label: "Containers",
  },
  {
    key: AGENT_ROUTES.masterContainers,
    icon: <FileTextOutlined />,
    label: "Master BL/AWB",
  },
];

const renderSidebar = (
  selectedKey: string,
  onNavigate: (key: string) => void,
  compact?: boolean
) => (
  <>
    <LogoArea $compact={compact}>
      <img
        src="/logo_black.png"
        alt="E-Freight Forwarding"
        style={{ filter: "brightness(0) invert(1)" }}
      />
      {!compact && <PortalLabel>Agent portal</PortalLabel>}
    </LogoArea>
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      items={navItems}
      onClick={({ key }) => onNavigate(key)}
    />
  </>
);

export const AgentLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { agent } = useAgentProfile();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate(AGENT_ROUTES.login);
  };

  const userMenu = {
    items: [
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Sign out",
        onClick: handleLogout,
      },
    ],
  };

  const selectedKey = location.pathname.startsWith(AGENT_ROUTES.masterContainers)
    ? AGENT_ROUTES.masterContainers
    : location.pathname.startsWith(AGENT_ROUTES.containers)
      ? AGENT_ROUTES.containers
      : AGENT_ROUTES.bookings;

  const handleNavigate = (key: string) => {
    navigate(key);
    setMobileMenuOpen(false);
  };

  return (
    <StyledLayout>
      <DesktopSider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="dark"
        width={180}
        collapsedWidth={64}
      >
        {renderSidebar(selectedKey, handleNavigate, collapsed)}
      </DesktopSider>

      <InnerLayout>
        <TopHeader>
          <HeaderStart>
            <MobileMenuButton
              type="text"
              aria-label="Open menu"
              icon={<MenuOutlined />}
              onClick={() => setMobileMenuOpen(true)}
            />
            <AgentName strong>Agent: {agent?.name || "—"}</AgentName>
          </HeaderStart>
          <Dropdown menu={userMenu} placement="bottomRight">
            <UserButton type="text" icon={<UserOutlined />}>
              {user?.email}
            </UserButton>
          </Dropdown>
        </TopHeader>
        <MainContent>
          <Outlet />
        </MainContent>
      </InnerLayout>

      <MobileDrawer
        placement="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        width={280}
        closable
        styles={{ body: { padding: 0 } }}
      >
        {renderSidebar(selectedKey, handleNavigate, false)}
      </MobileDrawer>
    </StyledLayout>
  );
};
